import {
  Alert,
  Button,
  Heading,
  Menu,
  MenuItem,
  MenuToggleButton,
  Tag,
  Text,
} from "@growtherapy/sprout-ui";

import { useFeatureGate } from "@statsig/react-bindings";
import { useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { ReactNode, useCallback, useEffect, useRef } from "react";
import {
  AppointmentTime,
  type AppointmentTimeProps,
} from "../../components/AppointmentTime";
import { logger } from "../../datadog";
import { sendLoggingEvents, TrackingEvents } from "../../events";
import { useGetTelehealthSessionInfo } from "../../hooks";
import { useVideoPlatform } from "../../hooks/useVideoPlatform";
import { unreadMessagesCountFamily } from "../../session/state";
import { delayedEntryGate } from "../../statsig/gates";
import { VisitorPresence, VisitorPresenceMap } from "../../types";
import {
  participantCountAtom,
  VideoPlatform,
  videoPlatformsMatch,
} from "../../video-sdk";
import { JoiningClientLoader } from "../admission/JoiningClientLoader";
import { useOpenConversationCallback } from "../chat/useOpenConversationCallback";
import { ClientMoodWrapper as ClientMood } from "../client-information/ClientMood";
import { hasNewFormsAtomFamily } from "../client-information/state";
import { useOpenClientInformationCallback } from "../client-information/useClientInformationDrawerCallbacks";
import {
  inSessionPatientInformationAtom,
  joiningVisitorIdFamily,
  newSessionConfirmationDataAtom,
  providerShortIdAtom,
  waitingRoomConversationSidsByVisitorIdAtom,
  waitingRoomConversationSidsByVisitorIdFamily,
  waitingVisitorsAtom,
} from "../state";
import { ClientActions } from "./ClientActions";
import { ClientActionsMenu } from "./ClientActionsMenu";
import { IncompatibleApplicationAdmitButton } from "./IncompatibleApplicationAdmitButton";
import { openWaitingRoomConversation } from "./openWaitingRoomConversation";
import { useAdmitClientCallback } from "./useAdmitClientCallback";

type ClientQueueActionsProps = {
  appointment?: AppointmentTimeProps["appointment"];
  clientNameAnnotation?: ReactNode;
  currentSessionPatientShortId: VisitorPresence["patientShortId"] | null;
  inSession?: boolean;
  isAppointmentLoading?: AppointmentTimeProps["isLoading"];
  isConversationLoading?: boolean;
  isClientInformationLoading?: boolean;
  providerCanSupportAllClients?: boolean;
  loading?: boolean;
  onChat?: () => void;
  onOpenClientInformationDrawer: () => void;
  onAdmit: () => void;
  onShowStartNewSessionConfirmation: () => void;
  unreadMessagesCount?: number;
  visitorName: string;
  visitorUuid: string;
  patientShortId: string;
  hideAdmitButton: boolean;
  joinRoomMode?: VisitorPresence["joinRoomMode"];
};

type ClientQueueActionButtonsProps = Pick<
  ClientQueueActionsProps,
  | "currentSessionPatientShortId"
  | "inSession"
  | "loading"
  | "onAdmit"
  | "onShowStartNewSessionConfirmation"
  | "patientShortId"
  | "providerCanSupportAllClients"
  | "visitorName"
>;
function ClientQueueActionButtons({
  currentSessionPatientShortId,
  inSession,
  loading,
  onAdmit,
  onShowStartNewSessionConfirmation,
  patientShortId,
  providerCanSupportAllClients,
  visitorName,
}: ClientQueueActionButtonsProps) {
  const isRequestForNewSession =
    !!currentSessionPatientShortId &&
    currentSessionPatientShortId !== patientShortId;

  if (!providerCanSupportAllClients) {
    return <IncompatibleApplicationAdmitButton />;
  }

  if (isRequestForNewSession) {
    return (
      <Button
        data-testid={`client-actions.admit:${visitorName}.end-and-start-new-session`}
        loading={loading}
        onClick={onShowStartNewSessionConfirmation}
      >
        Admit
      </Button>
    );
  }

  if (inSession) {
    return (
      <Menu
        placement="bottom end"
        buttonClassName="py-3 h-12"
        toggleButton={
          <MenuToggleButton
            data-testid={`client-actions.menu:${visitorName}`}
            use="primary"
            loading={loading}
          >
            Admit
          </MenuToggleButton>
        }
      >
        <MenuItem
          data-testid={`client-actions.menu:${visitorName}.admit-to-current-session`}
          onClick={() => {
            onAdmit();
            sendLoggingEvents(TrackingEvents.PROVIDER_ADMIT_CLIENT);
          }}
        >
          Admit to current session
        </MenuItem>
        <MenuItem
          data-testid={`client-actions.menu:${visitorName}.end-and-start-new-session`}
          onClick={() => onShowStartNewSessionConfirmation()}
        >
          End and start new session
        </MenuItem>
      </Menu>
    );
  }

  return (
    <Button
      data-testid={`client-actions.admit:${visitorName}`}
      loading={loading}
      onClick={onAdmit}
    >
      Admit
    </Button>
  );
}

export function ClientQueueActions({
  appointment,
  clientNameAnnotation: providedClientNameAnnotation,
  currentSessionPatientShortId,
  inSession,
  isAppointmentLoading,
  isConversationLoading,
  providerCanSupportAllClients = true,
  loading,
  onChat,
  onOpenClientInformationDrawer,
  onAdmit,
  onShowStartNewSessionConfirmation,
  patientShortId,
  unreadMessagesCount,
  visitorName,
  hideAdmitButton,
  joinRoomMode,
}: ClientQueueActionsProps) {
  const isRequestForNewSession =
    !!currentSessionPatientShortId &&
    currentSessionPatientShortId !== patientShortId;

  const clientNameAnnotation = (
    <>
      {isRequestForNewSession && (
        <AppointmentTime
          appointment={appointment}
          isLoading={isAppointmentLoading}
          variant="xs"
        />
      )}

      {providedClientNameAnnotation}
    </>
  );

  const hasNewForms = useAtomValue(hasNewFormsAtomFamily(patientShortId));

  return (
    <ClientActions
      clientName={visitorName}
      clientNameAnnotation={clientNameAnnotation}
      actions={
        loading && joinRoomMode === "manual" ? (
          <JoiningClientLoader />
        ) : (
          <div className="flex gap-2 shrink-0">
            {!hideAdmitButton && (
              <ClientQueueActionButtons
                currentSessionPatientShortId={currentSessionPatientShortId}
                inSession={inSession}
                loading={loading}
                onAdmit={onAdmit}
                onShowStartNewSessionConfirmation={
                  onShowStartNewSessionConfirmation
                }
                patientShortId={patientShortId}
                providerCanSupportAllClients={providerCanSupportAllClients}
                visitorName={visitorName}
              />
            )}
            {/* TODO: JRNY-1299 */}
            {/* <Button use="destructive">Deny</Button> */}
            {onChat && (
              <ClientActionsMenu
                visitorName={visitorName}
                loading={loading}
                isConversationLoading={isConversationLoading}
                patientShortId={patientShortId}
                unreadMessagesCount={unreadMessagesCount}
                onChat={onChat}
                onOpenClientInformationDrawer={onOpenClientInformationDrawer}
                hasNewClientInfo={hasNewForms}
              />
            )}
          </div>
        )
      }
    />
  );
}

export function ClientQueueActionsWrapper({
  visitorName,
  visitorUuid,
  patientShortId,
  visitorVideoPlatform,
  providerVideoPlatform,
  joinRoomMode,
}: Pick<
  ClientQueueActionsProps,
  "visitorName" | "patientShortId" | "joinRoomMode"
> & {
  visitorUuid: string;
  visitorVideoPlatform?: VideoPlatform | null;
  providerVideoPlatform?: VideoPlatform | null;
}) {
  const inSession = !!useAtomValue(participantCountAtom);
  const [admitClient] = useAdmitClientCallback();
  const setNewSessionConfirmationData = useSetAtom(
    newSessionConfirmationDataAtom,
  );

  const waitingRoomConversationSid = useAtomValue(
    waitingRoomConversationSidsByVisitorIdFamily(visitorUuid),
  );
  const { patientShortId: currentSessionPatientShortId = null } =
    useAtomValue(inSessionPatientInformationAtom) ?? {};
  const getWaitingRoomConversationSids = useAtomCallback(
    useCallback((get) => get(waitingRoomConversationSidsByVisitorIdAtom), []),
  );

  const isWaitingForClientToJoin = useAtomValue(
    joiningVisitorIdFamily(visitorUuid),
  );
  const { value: isDelayedEntry } = useFeatureGate(delayedEntryGate);
  const loading = isDelayedEntry ? false : isWaitingForClientToJoin;
  const openConversation = useOpenConversationCallback();

  const openClientInformation = useOpenClientInformationCallback();

  const openClientInformationSection = useCallback(() => {
    openClientInformation({ visitorUuid, patientShortId });
  }, [openClientInformation, visitorUuid, patientShortId]);

  const unreadMessagesCount = useAtomValue(
    unreadMessagesCountFamily(waitingRoomConversationSid),
  );

  const providerShortId = useAtomValue(providerShortIdAtom);
  const { data, loading: isAppointmentLoading } = useGetTelehealthSessionInfo(
    providerShortId,
    patientShortId,
  );
  const appointment = data?.telehealthSessionInfo?.appointment;

  const clientNameAnnotation = (
    <ClientMood variant="xs" visitorUuid={visitorUuid} />
  );

  const videoPlatformsAreMismatched = !videoPlatformsMatch(
    visitorVideoPlatform,
    providerVideoPlatform,
  );
  const providerCanSupportClient = !inSession || !videoPlatformsAreMismatched;

  return (
    <ClientQueueActions
      {...{
        appointment,
        clientNameAnnotation,
        currentSessionPatientShortId,
        inSession,
        isAppointmentLoading,
        isConversationLoading: !waitingRoomConversationSid,
        providerCanSupportAllClients: providerCanSupportClient,
        loading,
        onOpenClientInformationDrawer: openClientInformationSection,
        onChat: () => {
          openWaitingRoomConversation(
            visitorUuid,
            getWaitingRoomConversationSids,
            openConversation,
          );
        },
        onAdmit: async () => {
          admitClient({
            visitorId: visitorUuid,
            visitorName,
            patientShortId,
          });
        },
        onShowStartNewSessionConfirmation: () => {
          setNewSessionConfirmationData({
            visitorName,
            visitorUuid,
            patientShortId,
          });
        },
        unreadMessagesCount,
        visitorName,
        visitorUuid,
        patientShortId,
        hideAdmitButton: isDelayedEntry && isWaitingForClientToJoin,
        joinRoomMode,
      }}
    />
  );
}

type ClientQueueProps = {
  clients: VisitorPresenceMap;
  clientActionsComponent: typeof ClientQueueActionsWrapper;
  onAdmit?: (uuid: string, name: string) => void;
  loading?: boolean;
  providerVideoPlatform?: VideoPlatform | null;
};

export function ClientQueue({
  clients,
  clientActionsComponent: ClientActionsComponent,
  providerVideoPlatform,
}: ClientQueueProps) {
  const inSession = !!useAtomValue(participantCountAtom);
  const hasWaitingClients = Object.keys(clients).length;
  const numClientsInQueue = Object.keys(clients).length ?? 0;
  const providerCanSupportAllClients =
    !inSession ||
    Object.values(clients).every((client) =>
      videoPlatformsMatch(client.videoPlatform, providerVideoPlatform),
    );

  const hasLoggedRef = useRef(false);
  useEffect(() => {
    if (providerCanSupportAllClients || hasLoggedRef.current) return;

    hasLoggedRef.current = true;
    logger.warn("Application compatibility issue detected", {
      clientVideoPlatforms: Object.values(clients).map(
        (client) => client.videoPlatform,
      ),
      providerVideoPlatform,
    });
  }, [providerCanSupportAllClients, clients, providerVideoPlatform]);

  return (
    <>
      <div className="flex items-center gap-2">
        <Heading as="h3" variant="md">
          Client queue
        </Heading>
        <Tag
          aria-label={`${numClientsInQueue} waiting`}
          use="mauve"
          data-testid="client-queue.count"
        >
          {numClientsInQueue}
        </Tag>
      </div>
      {!providerCanSupportAllClients && (
        <Alert
          heading="Application compatibility issue"
          use="error"
          headingAs="h3"
        >
          <Text>
            Clients have incompatible application versions and cannot be
            admitted to the same session.
          </Text>
        </Alert>
      )}
      {hasWaitingClients ? (
        Object.entries(clients).map(
          ([
            visitorUuid,
            {
              name: visitorName = "",
              patientShortId,
              videoPlatform = VideoPlatform.Twilio,
              joinRoomMode,
            },
          ]) => (
            <ClientActionsComponent
              key={visitorUuid}
              {...{
                visitorName,
                visitorUuid,
                patientShortId,
                visitorVideoPlatform: videoPlatform,
                providerVideoPlatform,
                joinRoomMode,
              }}
            />
          ),
        )
      ) : (
        <Text>Clients who join your waiting room will appear here.</Text>
      )}
    </>
  );
}

export function ClientQueueWrapper() {
  const waitingClients = useAtomValue(waitingVisitorsAtom);
  const providerVideoPlatform = useVideoPlatform();

  return (
    <ClientQueue
      clients={waitingClients}
      clientActionsComponent={ClientQueueActionsWrapper}
      providerVideoPlatform={providerVideoPlatform}
    />
  );
}
